import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { getThemeClasses, getInputClasses, getButtonClasses, getCardClasses, getAlertClasses } from '../utils/themeUtils';

export default function ProfileDetails() {
  const navigate = useNavigate();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    business_name: '',
    mobile_phone: '',
    notification_preference: 'email',
    street_address_1: '',
    street_address_2: '',
    city: '',
    state_province: '',
    zip_postal_code: '',
    country: ''
  });

  // Mobile verification state
  const [mobileVerification, setMobileVerification] = useState({
    isVerified: false,
    codeSent: false,
    verificationCode: '',
    isSendingCode: false,
    isVerifying: false,
    canResend: true,
    resendTimer: 0
  });

  // Load user details on component mount
  useEffect(() => {
    const loadUserDetails = async () => {
      if (!isSignedIn) return;
      
      try {
        const token = await getToken();
        const response = await fetch('https://parrotmate.com/api/user/details', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load user details');
        }

        const userData = await response.json();
        
        // Update form data with user details
        setFormData({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          business_name: userData.business_name || '',
          mobile_phone: formatE164ToDisplay(userData.mobile_phone) || '',
          notification_preference: userData.notification_preference || 'email',
          street_address_1: userData.street_address_1 || '',
          street_address_2: userData.street_address_2 || '',
          city: userData.city || '',
          state_province: userData.state_province || '',
          zip_postal_code: userData.zip_postal_code || '',
          country: userData.country || ''
        });

        // Set mobile verification status
        setMobileVerification(prev => ({
          ...prev,
          isVerified: userData.mobile_phone_verified || false
        }));

      } catch (error) {
        console.error('Error loading user details:', error);
        setError('Failed to load user details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadUserDetails();
  }, [isSignedIn, getToken]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // If SMS is selected but no mobile phone is provided, switch to email
      if (name === 'notification_preference' && value === 'sms' && !prev.mobile_phone) {
        return {
          ...newData,
          notification_preference: 'email'
        };
      }
      
      return newData;
    });
    
    // Clear success message when user starts editing
    if (success) {
      setSuccess(false);
    }
  };

  // Convert E.164 format (+1XXXXXXXXXX) to display format ((XXX) XXX-XXXX)
  const formatE164ToDisplay = (e164Phone) => {
    if (!e164Phone) return '';
    
    // Remove +1 prefix and format the remaining 10 digits
    const digits = e164Phone.replace(/^\+1/, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    
    return e164Phone; // Return as-is if not in expected format
  };

  // Mobile phone verification functions
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return digits;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, mobile_phone: formatted }));
    
    // Reset verification status if phone number changes
    if (mobileVerification.isVerified) {
      setMobileVerification(prev => ({
        ...prev,
        isVerified: false,
        codeSent: false,
        verificationCode: ''
      }));
    }

    // Clear success message when user starts editing
    if (success) {
      setSuccess(false);
    }
  };

  const sendVerificationCode = async () => {
    if (!formData.mobile_phone || mobileVerification.isSendingCode) return;
    
    // Extract digits only for API call
    const phoneDigits = formData.mobile_phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setError('');
    setMobileVerification(prev => ({ ...prev, isSendingCode: true }));

    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/user/mobile/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mobile_phone: `+1${phoneDigits}`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send verification code');
      }

      setMobileVerification(prev => ({
        ...prev,
        codeSent: true,
        canResend: false,
        resendTimer: 120 // 2 minutes = 120 seconds
      }));

      // Start resend timer
      const timer = setInterval(() => {
        setMobileVerification(prev => {
          if (prev.resendTimer <= 1) {
            clearInterval(timer);
            return { ...prev, canResend: true, resendTimer: 0 };
          }
          return { ...prev, resendTimer: prev.resendTimer - 1 };
        });
      }, 1000);

    } catch (error) {
      console.error('Error sending verification code:', error);
      setError(error.message);
    } finally {
      setMobileVerification(prev => ({ ...prev, isSendingCode: false }));
    }
  };

  const verifyCode = async () => {
    if (!mobileVerification.verificationCode || mobileVerification.isVerifying) return;

    setError('');
    setMobileVerification(prev => ({ ...prev, isVerifying: true }));

    try {
      const token = await getToken();
      
      // Extract digits only for API call
      const phoneDigits = formData.mobile_phone.replace(/\D/g, '');
      
      const response = await fetch('https://parrotmate.com/api/user/mobile/confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mobile_phone: `+1${phoneDigits}`,
          verification_code: mobileVerification.verificationCode
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Invalid verification code');
      }

      setMobileVerification(prev => ({
        ...prev,
        isVerified: true,
        codeSent: false,
        verificationCode: ''
      }));

    } catch (error) {
      console.error('Error verifying code:', error);
      setError(error.message);
    } finally {
      setMobileVerification(prev => ({ ...prev, isVerifying: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate mobile phone if SMS notification is selected
    if (formData.notification_preference === 'sms' && (!formData.mobile_phone || !mobileVerification.isVerified)) {
      setError('Mobile phone must be verified to receive SMS notifications');
      return;
    }

    // Validate mobile phone if provided but not verified
    if (formData.mobile_phone && !mobileVerification.isVerified) {
      setError('Please verify your mobile phone number before saving');
      return;
    }

    setError('');
    setSaving(true);

    try {
      const token = await getToken();
      
      // Prepare update data
      const updateData = { ...formData };
      
      // Include mobile phone if provided and verified
      if (formData.mobile_phone && mobileVerification.isVerified) {
        // Convert formatted phone to E.164 format for storage
        const phoneDigits = formData.mobile_phone.replace(/\D/g, '');
        updateData.mobile_phone = `+1${phoneDigits}`;
      }
      
      const response = await fetch('https://parrotmate.com/api/update-profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      setSuccess(true);
      // Navigate back to profile page after successful save
      setTimeout(() => {
        navigate('/profile');
      }, 1500); // Give user time to see success message

    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Show loading screen while checking authentication
  if (!isLoaded || loading) {
    const theme = getThemeClasses();
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className={theme.text.secondary}>Loading profile details...</p>
        </div>
      </div>
    );
  }

  // Redirect if not signed in
  if (!isSignedIn) {
    navigate('/sign-in');
    return null;
  }

  const theme = getThemeClasses();

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link 
          to="/profile" 
          className={`inline-flex items-center mb-4 ${theme.text.link}`}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Profile
        </Link>
        <h1 className={`text-3xl font-bold ${theme.text.primary}`}>Profile Details</h1>
        <p className={`mt-2 ${theme.text.secondary}`}>
          Update your personal information and preferences
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className={`mb-6 ${getAlertClasses('success')}`}>
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="font-medium">Profile updated successfully!</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className={`mb-6 ${getAlertClasses('error')}`}>
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className={getCardClasses()}>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className={`text-lg font-medium mb-4 ${theme.text.primary}`}>Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                  First Name *
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  className={getInputClasses()}
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <label htmlFor="last_name" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                  Last Name *
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  className={getInputClasses()}
                  placeholder="Enter your last name"
                />
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div>
            <h3 className={`text-lg font-medium mb-4 ${theme.text.primary}`}>Business Information</h3>
            <div>
              <label htmlFor="business_name" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                Business Name
              </label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleInputChange}
                className={getInputClasses()}
                placeholder="Enter your business name (optional)"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className={`text-lg font-medium mb-4 ${theme.text.primary}`}>Contact Information</h3>
              <div className="space-y-4">
                {/* Mobile Phone Verification */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                    Mobile Phone {formData.notification_preference === 'sms' && '*'}
                  </label>
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <input
                        type="tel"
                        value={formData.mobile_phone}
                        onChange={handlePhoneChange}
                        className={getInputClasses(false, mobileVerification.isVerified)}
                        placeholder="(555) 123-4567"
                        maxLength={14}
                        disabled={mobileVerification.isVerified}
                      />
                      <button
                        type="button"
                        onClick={sendVerificationCode}
                        disabled={!formData.mobile_phone || mobileVerification.isSendingCode || !mobileVerification.canResend || mobileVerification.isVerified}
                        className={getButtonClasses(
                          formData.mobile_phone && mobileVerification.canResend && !mobileVerification.isSendingCode && !mobileVerification.isVerified
                            ? 'primary' : 'secondary',
                          'medium',
                          !formData.mobile_phone || mobileVerification.isSendingCode || !mobileVerification.canResend || mobileVerification.isVerified
                        )}
                      >
                        {mobileVerification.isVerified ? (
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-green-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Verified
                          </div>
                        ) : mobileVerification.isSendingCode ? (
                          'Sending...'
                        ) : !mobileVerification.canResend ? (
                          `Resend (${mobileVerification.resendTimer}s)`
                        ) : (
                          'Send Code'
                        )}
                      </button>
                    </div>
                    {mobileVerification.codeSent && !mobileVerification.isVerified && (
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={mobileVerification.verificationCode}
                          onChange={(e) => setMobileVerification(prev => ({ ...prev, verificationCode: e.target.value }))}
                          className={getInputClasses()}
                          placeholder="Enter 6-digit code"
                          maxLength={6}
                        />
                        <button
                          type="button"
                          onClick={verifyCode}
                          disabled={!mobileVerification.verificationCode || mobileVerification.isVerifying}
                          className={getButtonClasses('primary', 'medium', !mobileVerification.verificationCode || mobileVerification.isVerifying)}
                        >
                          {mobileVerification.isVerifying ? 'Verifying...' : 'Verify'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notification Preference */}
                <div>
                  <label htmlFor="notification_preference" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                    Notification Preference *
                  </label>
                  <select
                    id="notification_preference"
                    name="notification_preference"
                    value={formData.notification_preference}
                    onChange={handleInputChange}
                    required
                    className={getInputClasses()}
                  >
                    <option value="email">Email</option>
                    <option value="sms" disabled={!mobileVerification.isVerified}>SMS {!mobileVerification.isVerified && '(Verify phone first)'}</option>
                    <option value="both" disabled={!mobileVerification.isVerified}>Both {!mobileVerification.isVerified && '(Verify phone first)'}</option>
                  </select>
                </div>
              </div>
            </div>

          {/* Address Information */}
          <div>
            <h3 className={`text-lg font-medium mb-4 ${theme.text.primary}`}>Address Information</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="street_address_1" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                    Address Line 1 *
                  </label>
                  <input
                    type="text"
                    id="street_address_1"
                    name="street_address_1"
                    value={formData.street_address_1}
                    onChange={handleInputChange}
                    required
                    className={getInputClasses()}
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <label htmlFor="street_address_2" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    id="street_address_2"
                    name="street_address_2"
                    value={formData.street_address_2}
                    onChange={handleInputChange}
                    className={getInputClasses()}
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="city" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                      City *
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className={getInputClasses()}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label htmlFor="state_province" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                      State/Province *
                    </label>
                    <input
                      type="text"
                      id="state_province"
                      name="state_province"
                      value={formData.state_province}
                      onChange={handleInputChange}
                      required
                      className={getInputClasses()}
                      placeholder="State or Province"
                    />
                  </div>
                  <div>
                    <label htmlFor="zip_postal_code" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                      ZIP/Postal Code *
                    </label>
                    <input
                      type="text"
                      id="zip_postal_code"
                      name="zip_postal_code"
                      value={formData.zip_postal_code}
                      onChange={handleInputChange}
                      required
                      className={getInputClasses()}
                      placeholder="ZIP or Postal Code"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="country" className={`block text-sm font-medium mb-2 ${theme.text.secondary}`}>
                    Country *
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className={getInputClasses()}
                  >
                    <option value="">Select a country</option>
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="Italy">Italy</option>
                    <option value="Spain">Spain</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className={getButtonClasses('secondary', 'medium')}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className={getButtonClasses('primary', 'medium', saving)}
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
